import React, { useCallback, useEffect, useId, useState, useRef } from 'react';
import { debounce } from 'lodash';
import { Lang, KNOWLEDGE_TYPES, ROUTES, ENTITY_TYPES } from 'shared/constants';
import { useFormik } from 'formik';
import { DatePicker, Loader, FileUpload } from '../../core';
import analyticsService from '../../../helpers/analytics.service';
import { EVENTS } from 'shared/constants/analytics';
import sharedStyles from '../knowledge-content.module.scss';
import styles from './knowledge-content-editor.module.scss';
import { ConfirmationPopupComponent } from '../../common/confirmation-popup/confirmation-popup.component';
import { TableOfContents } from '../components/table-of-content';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGoBack } from 'src/hooks/use-go-back';
import { LabelsSelector } from '../../labels/labels-selector';
import { useHeadingsData } from '../components/table-of-content/table-of-contents.hooks';
import { useDispatch } from 'src/store/use-dispatch';
import {
  convertAssetToPdf,
  createKnowledgeLink,
} from 'shared/store/actions/knowledge.items.actions';
import { useSmartFormat } from './hooks/use-smart-format';
import { ASSET_TYPES } from 'shared/constants/assets.constants';
import uploadService from 'shared/services/upload.service';
import { getKnowledgeItemSchema, DUPLICATE_LINK_ERROR_CODE } from '../schema/knowledge-item.schema';
import { KnowledgeViewLayout } from '../layout/knowledge-view-layout';
import {
  RenderHeaderActions,
  RenderExpirationDate,
  RenderKeywordsSelector,
  RenderNotifyTeam,
  RenderEditor,
} from './components/';
import { RenderSection } from '../components';
import { RenderTags } from './components/render-tags';
import { TextInput, TextAreaInput } from 'src/components/design-system/forms';
import { RenderBack } from '../components/render-back';
import { ContributorsSelector } from './components';
import { useSelector } from 'react-redux';
import { getCategoryById, getItemCategory } from 'shared/store/selectors/knowledge.selector';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';
import { useContentPreview } from 'src/pages/application/knowledge-content/hooks';
import { notification } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk } from '@fortawesome/pro-light-svg-icons';
import { Button } from 'src/components/design-system';
import { getUser } from 'shared/store/selectors/user.selector';
import { useVettedOrProvidedLang } from 'shared/hooks/lang.hooks';
import { useConversion } from '../smart-format-conversion.context';
import { getLang } from 'shared/store/selectors/lang.selector';
import { isPublicContentProvider } from 'shared/store/selectors/organization.selector';
import {
  useDocumentEditor,
  useIsDocumentEditorSupported,
} from 'src/components/document-editor/hooks/use-document-editor';
import { getFileExtension, isSpreadsheetFile } from 'shared/utils';
import {
  isExternalSyncReadOnly,
  buildSyncedUpdatePayload,
  buildExpiresAtPayload,
  getSyncFixedTagFields,
} from 'shared/utils/knowledge.utils';
import knowledgeServiceApi from 'shared/api/knowledge.service.api';
import Logger from 'shared/utils/Logger';
import {
  DUPLICATED_UPLOAD_OUTCOME,
  handleDuplicateDetected,
  resolveDuplicatedUpload,
} from './duplicated-upload.helpers';

const editorLogger = Logger.create({ loggerName: 'KnowledgeContentEditor' });

const removeExtension = fileName => fileName?.slice(0, fileName.lastIndexOf('.')) || '';

/**
 * Wait for the OnlyOffice save callback to complete after destroying the editor.
 * Polls the knowledge item's fileUpdatedAt until it changes from the initial value.
 * @param {string} itemId - Knowledge item ID
 * @param {string | null} initialFileUpdatedAt - The fileUpdatedAt value before the save was triggered
 * @returns {Promise<boolean>} true if save detected, false on timeout
 */
async function waitForDocumentSave(itemId, initialFileUpdatedAt) {
  const maxWaitMs = 15000;
  const pollIntervalMs = 1000;
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    try {
      const res = await knowledgeServiceApi.getKnowledgeItemById({ id: itemId });
      const currentFileUpdatedAt = res?.data?.fileUpdatedAt || null;
      if (currentFileUpdatedAt && currentFileUpdatedAt !== initialFileUpdatedAt) {
        return true;
      }
    } catch (err) {
      editorLogger.warn('Error polling for document save', { error: err.message });
    }
  }

  editorLogger.warn('Timed out waiting for document save', { itemId, maxWaitMs });
  return false;
}

const KnowledgeContentEditorComponent = React.memo(function KnowledgeContentEditorComponent({
  lang = Lang.KNOWLEDGE_EDITOR,
  knowledge = {},
  categoryId = null,
  createKnowledgeItem = () => {},
  updateKnowledgeItem = () => {},
  updateKnowledgeItemThumbnail = () => {},
  uploadFile = () => {},
  onFinish = () => {},
  type,
  fileName,
  labelsViability,
  currentWorkspaceId,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { state } = useLocation();
  const ownersLabelId = useId();
  const [isLoading, setLoadingState] = React.useState(false);
  const [parentId, setParentId] = React.useState(categoryId);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [editorFocused, setEditorFocused] = React.useState(!!state?.initialData);
  const user = useSelector(getUser);
  const isOrgPublicContentProvider = useSelector(isPublicContentProvider);
  const category = useSelector(({ knowledge }) => getCategoryById(knowledge, { id: parentId }));
  const itemCategory = useSelector(state => getItemCategory(state, { id: knowledge?.id }));

  const { isHub, analyticsTriggers } = useContentPreview();
  const [targetWorkspaceId, setTargetWorkspaceId] = React.useState(currentWorkspaceId);
  const validatedAtLang = useVettedOrProvidedLang(lang.VALIDATED_AT);

  /**
   * @typedef {undefined | { knowledgeId: string }} DuplicatedAssetState
   */
  /**
   * @type {[DuplicatedAssetState, React.Dispatch<React.SetStateAction<DuplicatedAssetState>>]}
   */
  const [duplicatedAsset, setDuplicatedAsset] = React.useState();
  // reject-duplicate wants the uploaded asset's id, not the knowledge id the server points at
  const duplicatedUploadAssetIdRef = useRef(undefined);
  const uploadPopupLang = useSelector(getLang('KNOWLEDGE_UPLOAD_POPUP'));

  // Track if document editor content was modified to show auto-save banner
  const [isDocumentModified, setIsDocumentModified] = useState(false);
  const editorActionsRef = useRef(null);
  const expiresAtTouchedRef = useRef(false);
  const linkInputRef = useRef(null);

  const [savingDocument, setSavingDocument] = useState(false);

  const handleEditorReady = React.useCallback(actions => {
    editorActionsRef.current = actions;
  }, []);

  const handleDocumentModified = React.useCallback(modified => {
    if (modified) {
      setIsDocumentModified(true);
    }
  }, []);

  const isNew = !knowledge.id;
  const isParentShared = category?.shared?.isShared;

  // Externally-synced items are a one-way mirror: read-only except Labels (org
  // locations) + Share to Hub. Visually lock the other fields and, on save, persist
  // only the allowed fields (see onSubmit) so the backend read-only policy passes.
  const isReadOnly =
    !isNew &&
    (isExternalSyncReadOnly(knowledge?.options) || isExternalSyncReadOnly(itemCategory?.options));
  // `inert` blocks mouse AND keyboard interaction and removes the subtree from the a11y tree.
  const lockedFieldProps = isReadOnly ? { inert: true, style: { opacity: 0.55 } } : {};
  // Tags stay editable on synced items, except the ones the provider fixes
  const lockedTagFields = React.useMemo(
    () => (isReadOnly ? getSyncFixedTagFields(knowledge, itemCategory) : []),
    [isReadOnly, knowledge, itemCategory],
  );

  const onSubmit = React.useCallback(
    async data => {
      // Flush the OnlyOffice save BEFORE setLoadingState unmounts the editor.
      // destroy() is what deterministically triggers the save callback, so it must run
      // for new (unsaved) items too; only the fileUpdatedAt poll needs a real item id.
      if (editorActionsRef.current && isDocumentModified) {
        try {
          setSavingDocument(true);
          editorActionsRef.current.destroy();
          if (!isNew) {
            await waitForDocumentSave(knowledge.id, knowledge?.fileUpdatedAt);
          }
        } catch (error) {
          editorLogger.error('Error waiting for document save during submit', {
            error: error.message,
          });
        } finally {
          setSavingDocument(false);
        }
      }

      setLoadingState(true);

      // Formik swallows a rejected onSubmit, so a backend 400 must be shown from here
      const notifySaveFailed = error => {
        editorLogger.error('Failed to save knowledge item', { error: error.message });
        if (error.code === DUPLICATE_LINK_ERROR_CODE) {
          setFieldError('link', lang.DUPLICATE_LINK);
          // The link field may be above the fold on save.
          linkInputRef.current?.focus();
          setLoadingState(false);
          return;
        }
        notification.error({
          message: lang.SAVE_FAILED,
          description: error.message,
          placement: 'bottomRight',
        });
        setLoadingState(false);
      };

      const { specialty, subspecialty, contentType, targetAudience, expiresAt, ...restData } = data;

      const itemData = {
        ...restData,
        // Formik marks every field touched on submit, so the picker keeps its own flag.
        ...buildExpiresAtPayload({ expiresAt, touched: expiresAtTouchedRef.current }),
        tags: { specialty, subspecialty, contentType, targetAudience },
        content: data?.content?.replace(/[\r\n]+/g, ''),
      };
      if (itemData.link === undefined) {
        delete itemData.link;
      }
      if (itemData.shareToHub === undefined) {
        delete itemData.shareToHub;
      }
      if (itemData.thumbnail === undefined) {
        delete itemData.thumbnail;
      }

      if (isReadOnly) {
        // Synced content: only the user-editable fields go up — the backend enforces the
        // server-owned ones. The thumbnail has its own endpoint (a File never goes in the
        // JSON body), so upload it here just like the normal save does.
        const syncedThumbnail = itemData.thumbnail;

        // A provider-fixed tag is not the user's to send — the body carries only their edits
        const unlessLocked = (field, value) =>
          lockedTagFields.includes(field) ? undefined : value;

        const readOnlyUpdate = buildSyncedUpdatePayload({
          id: knowledge.id,
          labels: itemData.labels,
          shareToHub: itemData.shareToHub,
          contributors: itemData.contributors,
          thumbnail: syncedThumbnail instanceof File ? undefined : syncedThumbnail,
          specialty: unlessLocked('specialty', specialty),
          subspecialty: unlessLocked('subspecialty', subspecialty),
          contentType: unlessLocked('contentType', contentType),
          targetAudience: unlessLocked('targetAudience', targetAudience),
          // Only when the user actually asked to notify — an untouched toggle keeps these out
          // of the body entirely.
          ...(itemData.isSendNotification
            ? {
                isSendNotification: itemData.isSendNotification,
                notifyOnWorkingHours: itemData.notifyOnWorkingHours,
                recipientsTags: itemData.recipientsTags,
              }
            : {}),
        });

        try {
          if (syncedThumbnail instanceof File) {
            await updateKnowledgeItemThumbnail({ id: knowledge.id, thumbnail: syncedThumbnail });
          }
          await updateKnowledgeItem(readOnlyUpdate);
        } catch (error) {
          editorLogger.error('Failed to save synced item', { error: error.message });
          notification.error({ message: lang.SAVE_FAILED, placement: 'bottomRight' });
          setLoadingState(false);
          return;
        }

        const EVENT_DATA = {
          id: knowledge.id,
          title: knowledge?.title,
          ownerIds: knowledge?.ownerIds,
          type: knowledge?.type,
          specialty,
          subspecialty,
          contentType,
          targetAudience,
          is_new_editor: canRenderInEditor,
          original_format: getFileExtension(knowledge?.link) || null,
        };
        analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.UPDATE_KNOWLEDGE_ITEM, EVENT_DATA);
        analyticsService.track(EVENTS.CONTENT.UPDATE, {
          ...EVENT_DATA,
          entityType: ENTITY_TYPES.knowledge,
        });

        navigate(ROUTES.KNOWLEDGE_ITEMS.replace(':id', knowledge.id), { replace: true });
        onFinish(knowledge.id);
        setLoadingState(false);
        return;
      }

      let knowledgeId = knowledge.id;

      if (isNew) {
        // New item created
        itemData.categoryId = parentId;

        if (isParentShared) {
          itemData.isParentShared = true;
        }

        // Extract thumbnail before create call to avoid sending File object in JSON payload
        const thumbnail = itemData.thumbnail;
        if (thumbnail instanceof File) {
          delete itemData.thumbnail;
        }

        let res;
        try {
          res = await createKnowledgeItem(itemData, targetWorkspaceId);
        } catch (error) {
          notifySaveFailed(error);
          return;
        }
        knowledgeId = res?.id;

        // Upload thumbnail after item is created (needs the ID)
        if (thumbnail instanceof File) {
          await updateKnowledgeItemThumbnail({ id: knowledgeId, thumbnail });
        }

        const EVENT_DATA = {
          id: knowledgeId,
          title: itemData?.title,
          ownerIds: itemData?.ownerIds,
          type: itemData?.type,
          specialty: itemData?.tags?.specialty,
          subspecialty: itemData?.tags?.subspecialty,
          contentType: itemData?.tags?.contentType,
          targetAudience: itemData?.tags?.targetAudience,
          workspaceId: targetWorkspaceId,
          parentCategoryId: parentId,
        };
        analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.CREATE_KNOWLEDGE_ITEM, EVENT_DATA);
        analyticsService.track(EVENTS.CONTENT.UPLOAD, {
          ...EVENT_DATA,
          entityType: ENTITY_TYPES.knowledge,
        });

        if (isHub) {
          notification.success({
            title: (
              <div className={styles.notificationMessage}>
                <div className={styles.bold}>{`"${itemData.title}"`}</div>
                <div>{lang.PUBLISHED_TO}</div>
                <div className={styles.bold}>{` ${category.title}`}</div>
              </div>
            ),
            btn: (
              <Button
                type="link"
                onClick={() => navigate(ROUTES.KNOWLEDGE_ITEMS.replace(':id', knowledgeId))}>
                {lang.VIEW_IN_KNOWLEDGE_BASE}
              </Button>
            ),
            placement: 'bottomRight',
          });
          const PUBLISH_EVENT_DATA = { ...EVENT_DATA, srcItemId: itemData?.hubInfo?.srcItemId };

          analyticsTriggers?.onPublish(PUBLISH_EVENT_DATA);

          if (dirty) {
            analyticsTriggers?.onImportEditContent(PUBLISH_EVENT_DATA);
          }

          onFinish(knowledgeId);
          setLoadingState(false);
          goBack();
          return;
        }
      } else {
        //Update knowledge by id
        itemData.id = knowledge.id;

        if (parentId && parentId !== categoryId) {
          itemData.categoryId = parentId;
        }

        const EVENT_DATA = {
          id: itemData?.id,
          title: itemData?.title,
          ownerIds: itemData?.ownerIds,
          type: itemData?.type,
          specialty: itemData?.tags?.specialty,
          subspecialty: itemData?.tags?.subspecialty,
          contentType: itemData?.tags?.contentType,
          targetAudience: itemData?.tags?.targetAudience,
          is_new_editor: canRenderInEditor,
          original_format: getFileExtension(knowledge?.link) || null,
        };

        analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.UPDATE_KNOWLEDGE_ITEM, EVENT_DATA);
        analyticsService.track(EVENTS.CONTENT.UPDATE, {
          ...EVENT_DATA,
          entityType: ENTITY_TYPES.knowledge,
        });

        // The thumbnail has its own endpoint and uploads only after the update is
        // accepted, so a rejected save (e.g. a duplicate link) leaves it unchanged.
        const thumbnail = itemData.thumbnail;
        if (thumbnail instanceof File) {
          delete itemData.thumbnail;
        }

        try {
          await updateKnowledgeItem(itemData);
          if (thumbnail instanceof File) {
            await updateKnowledgeItemThumbnail({ id: itemData.id, thumbnail });
          }
        } catch (error) {
          notifySaveFailed(error);
          return;
        }
      }

      navigate(ROUTES.KNOWLEDGE_ITEMS.replace(':id', knowledgeId), { replace: true });
      onFinish(knowledgeId);
      setLoadingState(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      knowledge.id,
      isNew,
      onFinish,
      parentId,
      createKnowledgeItem,
      categoryId,
      updateKnowledgeItem,
      updateKnowledgeItemThumbnail,
      isParentShared,
      isHub,
      lang,
      category,
      targetWorkspaceId,
      analyticsTriggers,
      isDocumentModified,
      // The sync flag arrives from Redux after mount, so a stale closure here would
      // submit the full payload for synced content.
      isReadOnly,
      lockedTagFields,
    ],
  );

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldError,
    resetForm,
    validateForm,
    values,
    errors,
    touched,
    initialValues,
    dirty,
    isValid,
  } = useFormik({
    validationSchema: getKnowledgeItemSchema({
      langEditor: useSelector(getLang('KNOWLEDGE_EDITOR')),
    }),
    initialValues: {
      type,
      ...knowledge,
      ...knowledge.tags,
      contributors: knowledge.contributors || [],
      fileUpdatedAt: new Date().toISOString(),
      expiresAt: knowledge.expiresAt ?? null,
      isSendNotification: false,
      notifyOnWorkingHours: false,
      recipientsTags: {},
      workspaceIds: [currentWorkspaceId],
      shareToHub:
        knowledge?.shared?.isShared ||
        ((isParentShared || isOrgPublicContentProvider) && isNew ? true : undefined),
      ownerIds: isHub ? [user.id] : isNew ? [] : (knowledge?.ownerIds ?? []),
    },
    onSubmit,
  });

  // need to force form validation when ownerIds change, sometimes the selector doesn't trigger it
  useEffect(() => {
    validateForm();
  }, [validateForm, values.ownerIds]);

  const setExpiresAt = useCallback(
    (name, value) => {
      expiresAtTouchedRef.current = true;
      setFieldValue(name, value);
    },
    [setFieldValue],
  );

  const { convertToSmartFormat } = useSmartFormat();
  const { clearAssetToasts, setEditorDirtyState } = useConversion();
  const conversionAbortController = useRef(null);
  const conversionLang = useSelector(getLang('BACKGROUND_CONVERSION'));
  const actionTooltipLang = useSelector(getLang('ACTION_BUTTON_TOOLTIPS'));

  useEffect(() => {
    return () => conversionAbortController.current?.abort();
  }, []);

  useEffect(() => {
    setEditorDirtyState(dirty || isDocumentModified);
    return () => setEditorDirtyState(false);
  }, [dirty, isDocumentModified, setEditorDirtyState]);

  const documentContent = values.content;
  const [debouncedContent, setDebouncedContent] = useState(documentContent);
  const debouncedSetContent = useRef(
    debounce(setDebouncedContent, 300, { leading: true, trailing: true }),
  ).current;

  useEffect(() => {
    debouncedSetContent(documentContent);
  }, [documentContent, debouncedSetContent]);

  useEffect(() => {
    return () => debouncedSetContent.cancel();
  }, [debouncedSetContent]);

  const isContent = values.type === KNOWLEDGE_TYPES.CONTENT;
  const isUploadedFile =
    values.type === KNOWLEDGE_TYPES.DOCUMENT ||
    values.type === KNOWLEDGE_TYPES.PDF ||
    values.type === KNOWLEDGE_TYPES.VIDEO ||
    values.type === KNOWLEDGE_TYPES.IMAGE;

  const { canRenderInEditor } = useDocumentEditor(values.link, values.type);
  const isDocumentEditorSupported = useIsDocumentEditorSupported();
  const isDocumentEditorActive = canRenderInEditor && !isNew;

  /*
   * Sets default title based on content's first heading or file name.
   * Only applies to the initial result of convertion.
   * If content is edited later, default title shouldn't change.
   */
  const { headings } = useHeadingsData({ content: knowledge?.content });

  useEffect(() => {
    let itemTitle = knowledge?.title;

    if (itemTitle) {
      return;
    }

    if (isContent) {
      itemTitle = headings?.[0]?.text || removeExtension(fileName);
    }
    if (isUploadedFile) {
      itemTitle = removeExtension(fileName);
    }

    setFieldValue('title', itemTitle);
  }, [knowledge?.title, fileName, headings, setFieldValue, isContent, isUploadedFile]);

  const openConfirmationPopup = React.useCallback(() => {
    setShowConfirmation(true);
  }, [setShowConfirmation]);

  const closeConfirmationPopup = React.useCallback(() => {
    setShowConfirmation(false);
    setBlockedLocation(null);
  }, [setShowConfirmation]);

  const handleLeave = async () => {
    closeConfirmationPopup();

    // Flush the OnlyOffice save before navigating. destroy() triggers the save for
    // unsaved items too; only the fileUpdatedAt poll needs a real item id.
    if (editorActionsRef.current && isDocumentModified) {
      try {
        setSavingDocument(true);
        editorActionsRef.current.destroy();
        if (knowledge.id) {
          await waitForDocumentSave(knowledge.id, knowledge?.fileUpdatedAt);
        }
      } catch (error) {
        editorLogger.error('Error waiting for document save during leave', {
          error: error.message,
        });
      } finally {
        setSavingDocument(false);
      }
    }

    resetForm(initialValues);
    if (blockedLocation) {
      // Navigate to blocked location
      navigate(blockedLocation.pathname, { state: blockedLocation.state, replace: true });
      clearAssetToasts(blockedLocation?.state?.initialData?.assetId);
      setBlockedLocation(null);
    } else {
      if (isHub) {
        analyticsTriggers?.onImportDismiss({
          id: knowledge?.id,
          title: knowledge?.title,
          ownerIds: knowledge?.ownerIds,
          type,
        });
      } else {
        analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.CLOSE_EDIT_MODE, {
          id: knowledge?.id,
          title: knowledge?.title,
          ownerIds: knowledge?.ownerIds,
          type,
          action: 'dismiss',
          state: isNew ? 'new' : 'edit',
        });
      }

      goBack();
      onFinish(knowledge.id);
    }
  };

  const [blockedLocation, setBlockedLocation] = useState(null);

  // Block browser navigation (close/refresh) with beforeunload
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (dirty || isDocumentModified) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dirty, isDocumentModified]);

  const resetField = React.useCallback(
    field => {
      // Clear the dependent field through Formik. resetForm() repointed initialValues at the
      // (mutated) current values, which dropped `dirty` and — with enableReinitialize — made the
      // next effect snap the whole form back to the entity, reverting the value the user had just
      // cleared. That made the specialty clear-X a no-op. See BB-17.
      setFieldValue(field, null);
    },
    [setFieldValue],
  );

  const onUploadFile = React.useCallback(
    async file => {
      setLoadingState(true);

      let newAsset = await uploadFile({ file });

      // finalize upload
      const finalizeRes = await uploadService.finalizeUploadFile({ assetId: newAsset?.assetId });

      if (finalizeRes?.error) {
        setLoadingState(false);
        return;
      }

      // Check for duplicates
      if (finalizeRes?.duplicate) {
        analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.DUPLICATION_DETECTED);
        const outcome = await handleDuplicateDetected({
          duplicatedKnowledgeId: finalizeRes.duplicate.knowledgeId,
          currentKnowledgeId: knowledge?.id,
          uploadedAssetId: newAsset?.assetId,
          rejectDuplicatedItem: args => uploadService.rejectDuplicatedItem(args),
          openDuplicatedBar: () => {
            duplicatedUploadAssetIdRef.current = newAsset?.assetId;
            setDuplicatedAsset(finalizeRes.duplicate);
          },
        });
        setLoadingState(false);
        if (outcome === DUPLICATED_UPLOAD_OUTCOME.SAME_ITEM) {
          notification.info({ message: lang.DUPLICATE_SAME_ITEM, placement: 'bottomRight' });
        }
        return;
      }

      // Spreadsheets stay in their original format only when the Document Editor
      // can render them; otherwise convert to PDF so a preview is available
      const skipPdfConversion = isDocumentEditorSupported && isSpreadsheetFile(file?.name);

      if (!skipPdfConversion) {
        const pdfAsset = await dispatch(convertAssetToPdf({ assetId: newAsset?.assetId }));

        // asset was converted to pdf
        if (pdfAsset) {
          newAsset = {
            ...newAsset,
            url: pdfAsset?.convertedFilePath,
            type: pdfAsset?.newFormat,
          };
        }
      }

      if (
        isContent &&
        (newAsset.type === ASSET_TYPES.DOCUMENT || newAsset.type === ASSET_TYPES.PDF)
      ) {
        conversionAbortController.current?.abort();
        conversionAbortController.current = new AbortController();

        const smartFormatConversionResult = await convertToSmartFormat({
          knowledgeId: knowledge?.id,
          knowledgeTitle: values.title,
          asset: newAsset,
          signal: conversionAbortController.current.signal,
        });

        if (smartFormatConversionResult.data) {
          setLoadingState(false);

          setFieldValue('type', KNOWLEDGE_TYPES.CONTENT);
          setFieldValue('content', smartFormatConversionResult.data);
          setFieldValue('link', newAsset?.url);

          return;
        }

        setLoadingState(false);
        notification.error({
          message: conversionLang.FAILED_TITLE,
          description: conversionLang.FAILED_DESCRIPTION.replace(
            '{fileName}',
            values.title || fileName,
          ),
          placement: 'bottomLeft',
          btn: (
            <Button size="small" type="danger" onClick={() => onUploadFile(file)}>
              {conversionLang.TRY_AGAIN}
            </Button>
          ),
        });
        return;
      }

      setLoadingState(false);

      setFieldValue('type', newAsset.type);
      setFieldValue('content', undefined);
      setFieldValue('link', newAsset?.url);
    },
    [
      uploadFile,
      dispatch,
      isContent,
      setFieldValue,
      convertToSmartFormat,
      conversionLang,
      knowledge?.id,
      values.title,
      fileName,
      isDocumentEditorSupported,
      lang.DUPLICATE_SAME_ITEM,
    ],
  );

  const closeDuplicatedAssetDialog = () => {
    duplicatedUploadAssetIdRef.current = undefined;
    setDuplicatedAsset(undefined);
  };

  const onDuplicatedAssetCancel = async () => {
    await uploadService.rejectDuplicatedItem({ assetId: duplicatedUploadAssetIdRef.current });
    closeDuplicatedAssetDialog();
  };

  const onDuplicatedAssetLink = async () => {
    setLoadingState(true);

    const outcome = await resolveDuplicatedUpload({
      duplicatedKnowledgeId: duplicatedAsset.knowledgeId,
      currentKnowledgeId: knowledge?.id,
      uploadedAssetId: duplicatedUploadAssetIdRef.current,
      categoryId: parentId,
      itemCategoryId: itemCategory?.id,
      ownerId: user.id,
      createLink: args => dispatch(createKnowledgeLink(args)),
      rejectDuplicatedItem: args => uploadService.rejectDuplicatedItem(args),
    });

    closeDuplicatedAssetDialog();
    setLoadingState(false);

    if (outcome === DUPLICATED_UPLOAD_OUTCOME.SAME_ITEM) {
      notification.info({ message: lang.DUPLICATE_SAME_ITEM, placement: 'bottomRight' });
      return;
    }

    if (outcome === DUPLICATED_UPLOAD_OUTCOME.LINK_FAILED) {
      notification.error({
        message: uploadPopupLang.DUPLICATION_LINK_FAILED,
        placement: 'bottomRight',
      });
      return;
    }

    // Nothing changes on screen for the edited item, so the toast stays longer and
    // takes the user to the linked item. The static antd notification renders outside
    // the Router, so this is a plain anchor that navigates in-app on click.
    const linkedItemPath = ROUTES.KNOWLEDGE_ITEMS.replace(':id', duplicatedAsset.knowledgeId);
    const toastKey = `duplicate-link-${duplicatedAsset.knowledgeId}`;
    notification.success({
      key: toastKey,
      message: uploadPopupLang.DUPLICATION_LINK_CREATED,
      description: (
        <a
          href={linkedItemPath}
          onClick={e => {
            e.preventDefault();
            notification.destroy(toastKey);
            navigate(linkedItemPath);
          }}>
          {uploadPopupLang.DUPLICATION_LINK_VIEW}
        </a>
      ),
      duration: 10,
      placement: 'bottomRight',
    });
  };

  const renderTableOfContents = () => <TableOfContents content={debouncedContent} />;

  const handleBack = React.useCallback(() => {
    if (dirty || isDocumentModified) {
      openConfirmationPopup();
    } else {
      goBack();
    }
  }, [dirty, isDocumentModified, goBack, openConfirmationPopup]);

  const renderHeader = () => {
    return (
      <>
        <div className={sharedStyles.contentHeader}>
          <div className={styles.header}>
            <RenderBack close={handleBack} className={styles.back} disabled={savingDocument} />
            <TextAreaInput
              name="title"
              value={values.title}
              error={errors.title}
              touched={touched.title?.toString()}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isReadOnly}
              autoSize={{ minRows: 1, maxRows: 2 }}
              placeholder={lang.ITEM_NAME_PLACEHOLDER}
              size="large"
              className={sharedStyles.titleInput}
              variant="borderless"
            />
          </div>
          <TextInput
            name="subtitle"
            value={values.subtitle}
            error={errors.subtitle}
            touched={touched.subtitle}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isReadOnly}
            size="small"
            placeholder={lang.ITEM_DESCRIPTION_PLACEHOLDER}
            variant="borderless"
            className={sharedStyles.subtitleInput}
          />
        </div>
        {isDocumentModified && (
          <div className={styles.autoSaveBanner}>
            <FontAwesomeIcon icon={faFloppyDisk} className={styles.autoSaveIcon} />
            <span>{lang.DOCUMENT_AUTO_SAVE}</span>
          </div>
        )}
      </>
    );
  };

  const onSelectBinder = ({ binderId, targetWorkspaceId }) => {
    setParentId(binderId);
    setTargetWorkspaceId(targetWorkspaceId);
  };

  const renderRightPanel = () => {
    return (
      <div className={styles.rightPanelWrapper}>
        <RenderNotifyTeam values={values} errors={errors} setFieldValue={setFieldValue} />

        <section className={sharedStyles.knowledgeActions}>
          {isDocumentEditorActive && (
            <p className={styles.documentEditorDisclaimer}>{lang.DOCUMENT_AUTO_SAVE_DISCLAIMER}</p>
          )}
          <RenderHeaderActions
            knowledgeId={knowledge.id}
            isLoading={isLoading || savingDocument}
            editorFocused={editorFocused}
            values={values}
            errors={errors}
            initialValues={initialValues}
            openConfirmationPopup={openConfirmationPopup}
            onRevertConversion={() => {
              setFieldValue('content', '');
              setFieldValue('type', KNOWLEDGE_TYPES.PDF);
            }}
            onSelectBinder={onSelectBinder}
            onSubmit={handleSubmit}
            isValid={isValid}
            dirty={dirty}
          />
        </section>

        <div {...lockedFieldProps}>
          <RenderSection header={validatedAtLang}>
            <div className={styles.inlineInput}>
              <DatePicker
                name="fileUpdatedAt"
                value={values.fileUpdatedAt}
                error={errors.fileUpdatedAt}
                touched={touched.fileUpdatedAt}
                onChange={setFieldValue}
                className={styles.smallWrapper}
                preventFutureDate
              />
            </div>
          </RenderSection>

          <RenderSection header={lang.OWNER} headerId={ownersLabelId}>
            <label>
              {errors.ownerIds && <span className={styles.error}>({errors.ownerIds})</span>}
            </label>
            <MultipleOwnersSelector
              value={values.ownerIds}
              placeholder={lang.OWNER_PLACEHOLDER}
              name="ownerIds"
              aria-labelledby={ownersLabelId}
              onChange={setFieldValue}
              borderRadius={4}
              parentOwnerIds={knowledge?.parentOwnerIds}
            />
          </RenderSection>
        </div>

        {/* Contributors and tags are C8-side classification the provider doesn't own, so
            they stay editable on synced items. */}
        <RenderSection header={lang.CONTRIBUTORS}>
          <ContributorsSelector
            onChange={v => setFieldValue('contributors', v)}
            error={errors.contributors}
            value={values.contributors}
            borderRadius={4}
          />
        </RenderSection>

        <RenderTags
          selectedValues={values}
          errors={errors}
          onChange={setFieldValue}
          resetField={resetField}
          lockedFields={lockedTagFields}
          lockedTooltip={actionTooltipLang.READ_ONLY_SYNCED}
        />

        <div {...lockedFieldProps}>
          <RenderKeywordsSelector
            setFieldValue={setFieldValue}
            keywords={values?.keywords}
            knowledgeKeywords={knowledge?.keywords}
          />
        </div>

        {labelsViability && (
          <RenderSection header={lang.LABELS}>
            <LabelsSelector
              value={values.labels}
              error={errors.labels}
              placeholder={lang.LABELS_PLACEHOLDER}
              name="labels"
              onChange={setFieldValue}
            />
          </RenderSection>
        )}

        {/* Deliberately outside lockedFieldProps: `inert` swallows hover, and this block
            carries its own disabled state and tooltip. */}
        <div>
          <RenderExpirationDate
            setFieldValue={setExpiresAt}
            name="expiresAt"
            value={values.expiresAt}
            error={errors.expiresAt}
            touched={touched.expiresAt}
            disabled={isReadOnly}
            disabledTooltip={actionTooltipLang.READ_ONLY_SYNCED}
          />
        </div>

        {/* The thumbnail is C8-side and uploads through its own endpoint, so it stays
            editable on synced items. */}
        <RenderSection header={lang.FEATURED_IMAGE}>
          <FileUpload
            name="thumbnail"
            onChange={setFieldValue}
            accept={{ 'image/*': [] }}
            wrapperClass={sharedStyles.thumbnailEditor}
            value={values.thumbnail}
            preview
            allowClear
          />
        </RenderSection>

        <div className={sharedStyles.footer}>
          {isLoading && <Loader />}
          {!isNew && <span className={sharedStyles.itemId}>Id: {knowledge?.id}</span>}
        </div>
      </div>
    );
  };

  return (
    <form className={styles.rootForm} onSubmit={handleSubmit}>
      <KnowledgeViewLayout
        hideLeftPanel={!isContent}
        renderLeftPanel={renderTableOfContents}
        renderHeader={renderHeader}
        renderRightPanel={renderRightPanel}
        knowledgeId={knowledge.id}>
        <div className={styles.content}>
          {savingDocument && (
            <div className={styles.savingOverlay}>
              <Loader />
              <p className={styles.savingText}>{lang.SAVING_DOCUMENT}</p>
            </div>
          )}
          <div {...lockedFieldProps}>
            <RenderEditor
              knowledgeId={knowledge?.id}
              isLoading={isLoading}
              values={values}
              errors={errors}
              touched={touched}
              duplicatedAsset={duplicatedAsset}
              onDuplicatedAssetCancel={onDuplicatedAssetCancel}
              onDuplicatedAssetLink={onDuplicatedAssetLink}
              onFileChange={onUploadFile}
              onContentChange={content => {
                setFieldValue('content', content);
              }}
              setLoadingState={setLoadingState}
              setEditorFocused={setEditorFocused}
              handleChange={handleChange}
              handleBlur={handleBlur}
              onDocumentModified={handleDocumentModified}
              onEditorReady={handleEditorReady}
              allowFileReplace={!isReadOnly}
              linkInputRef={linkInputRef}
            />
          </div>
        </div>
        <ConfirmationPopupComponent
          open={showConfirmation}
          title={lang.EXIT_TITLE}
          message={lang.EXIT_MESSAGE}
          onConfirm={handleLeave}
          onDismiss={closeConfirmationPopup}
          confirmMessage={lang.EXIT_CONFIRM}
          dismissMessage={lang.EXIT_DISMISS}
          confirmButtonStyle="primary"
          dismissButtonStyle="secondary"
        />
      </KnowledgeViewLayout>
    </form>
  );
});

export { KnowledgeContentEditorComponent };
